/**
 * Routewijzer "Wat past bij jou?".
 *
 * Alle zes de vraagschermen en alle zes de uitslagen staan al in de HTML; dit script
 * laat er telkens één zien. Geen framework en geen serverronde per vraag: dat houdt de
 * test snel genoeg om binnen anderhalve minuut af te ronden.
 *
 * Voortgang staat in sessionStorage, zodat iemand die per ongeluk terugnavigeert niet
 * opnieuw begint. Elke stap krijgt daarnaast een eigen URL via history.pushState, zodat
 * de terugknop doet wat mensen verwachten en de meting per vraag een pageview ziet.
 *
 * Antwoorden gaan pas ergens heen als iemand het formulier invult. Wie de test doet en
 * wegklikt, laat niets achter.
 */

import gsap from 'gsap';

import { EASE, minderBeweging } from './hero-motion';

const OPSLAG = 'osnabrugge-routewijzer';

// De pauze tussen een antwoord en de volgende vraag. Lang genoeg om de letter te zien
// omklappen naar een vinkje -- anders lijkt het scherm te wisselen voordat je geklikt
// hebt -- en kort genoeg om niet als wachten te voelen.
const BEVESTIGING = 280;
const BEVESTIGING_RUSTIG = 120;

/**
 * Zet de kop om in losse woorden onder een masker, zodat ze er één voor één onder vandaan
 * kunnen komen. Gebeurt één keer per kop; daarna staan de spans er en levert dit ze
 * alleen nog op.
 *
 * De spatie blijft een echt spatieteken tussen de maskers: die zorgt voor de woordafstand
 * en breekt de regel op de goede plek. Met marges zou de kop bij een andere schermbreedte
 * anders afbreken dan hij hoort.
 *
 * @returns {Element[]}
 */
function woordenVan(kop) {
  if (!kop) {
    return [];
  }

  if (kop.dataset.quizGesplitst) {
    return [...kop.querySelectorAll('.quiz__word')];
  }

  const woorden = kop.textContent.trim().split(/\s+/);

  kop.textContent = '';

  const spans = woorden.map((woord, i) => {
    const masker = document.createElement('span');
    const inhoud = document.createElement('span');

    masker.className = 'quiz__word-mask';
    inhoud.className = 'quiz__word';
    inhoud.textContent = woord;

    masker.appendChild(inhoud);
    kop.appendChild(masker);

    if (i < woorden.length - 1) {
      kop.appendChild(document.createTextNode(' '));
    }

    return inhoud;
  });

  kop.dataset.quizGesplitst = '1';

  return spans;
}

export default function initQuiz() {
  const root = document.querySelector('[data-quiz]');

  if (!root) {
    return;
  }

  const stappen = [...root.querySelectorAll('[data-quiz-step]')];
  const uitslagen = [...root.querySelectorAll('[data-quiz-result]')];
  const balk = root.querySelector('[data-quiz-progress-bar]');
  const label = root.querySelector('[data-quiz-progress-label]');
  const voortgang = root.querySelector('.quiz__progress');

  // Een gedeelde uitslag-URL rendert alleen die ene uitslag; dan is er niets te sturen.
  if (!stappen.length) {
    vulFormulieren(lees());

    return;
  }

  const antwoorden = lees();
  let huidig = 1;

  // Bij het allereerste scherm valt er niets uit te laten lopen: dan zou je stap 1 zien
  // wegschuiven voor iemand die halverwege terugkomt.
  let gestart = false;

  // Tussen het antwoord en het volgende scherm staat de oude vraag er nog: het vinkje
  // klapt om en daarna loopt de vraag weg. In die paar tienden zijn de knoppen nog
  // aanklikbaar, en een tweede klik zou een vraag overslaan. Vandaar een slot dat pas
  // opengaat als het volgende scherm er staat.
  let bezig = false;

  function lees() {
    try {
      return JSON.parse(sessionStorage.getItem(OPSLAG)) || {};
    } catch (e) {
      return {};
    }
  }

  function bewaar() {
    try {
      sessionStorage.setItem(OPSLAG, JSON.stringify(antwoorden));
    } catch (e) {
      // Privémodus of opslag vol: de test werkt gewoon door, alleen niet hervatbaar.
    }
  }

  function meet(naam, extra = {}) {
    // dataLayer is er alleen als er een tagmanager draait; zonder blijft dit stil.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: naam, ...extra });
  }

  /**
   * De binnenkomst van een vraagscherm: de kop komt woord voor woord onder zijn masker
   * vandaan, de antwoorden komen er van onderaf achteraan, en de knoppen sluiten de rij.
   *
   * Op vaste tijdstippen en niet aaneengeschakeld: valt er een onderdeel weg -- vraag zes
   * heeft geen terugkoppeling -- dan verschuift de rest daar niet van.
   */
  function animeerIn(stap) {
    if (minderBeweging()) {
      return;
    }

    const woorden = woordenVan(stap.querySelector('.quiz__question'));
    const opties = [...stap.querySelectorAll('[data-quiz-option]')];
    const staart = [stap.querySelector('.quiz__hint'), stap.querySelector('[data-quiz-next]')].filter(Boolean);

    const tijdlijn = gsap.timeline();

    if (woorden.length) {
      tijdlijn.fromTo(woorden, { yPercent: 115 }, {
        yPercent: 0, duration: .9, ease: EASE, stagger: .045,
      }, 0);
    }

    if (opties.length) {
      tijdlijn.fromTo(opties, { y: 24, autoAlpha: 0 }, {
        y: 0, autoAlpha: 1, duration: .75, ease: EASE, stagger: .07,
      }, .18);
    }

    if (staart.length) {
      tijdlijn.fromTo(staart, { y: 12, autoAlpha: 0 }, {
        y: 0, autoAlpha: 1, duration: .6, ease: EASE, stagger: .06,
      }, .38);
    }
  }

  /**
   * Het vorige scherm loopt kort omhoog weg voordat het volgende binnenkomt. Kort
   * gehouden: dit zit tussen een klik en wat je wilt zien, dus het mag niet in de weg
   * gaan zitten.
   */
  function animeerUit(stap, klaar) {
    const inhoud = [stap.querySelector('.quiz__question'), stap.querySelector('.quiz__options'), stap.querySelector('.quiz__nav')].filter(Boolean);

    if (minderBeweging() || !inhoud.length) {
      klaar();

      return;
    }

    // De volgende vraag hangt aan het einde van deze animatie, en een animatie is geen
    // harde belofte: een browser die de animatielus stilzet -- een tab op de achtergrond
    // bijvoorbeeld -- laat het einde nooit vallen. Dan blijft de test hangen op een klik
    // die wel degelijk is aangekomen. Vandaar een vangnet dat hetzelfde doet, en een slot
    // zodat het er maar één keer doorheen komt.
    let gedaan = false;

    const verder = () => {
      if (gedaan) {
        return;
      }

      gedaan = true;

      gsap.set(inhoud, { clearProps: 'all' });
      klaar();
    };

    gsap.to(inhoud, {
      y: -16,
      autoAlpha: 0,
      duration: .28,
      ease: 'power2.in',
      stagger: .04,
      overwrite: 'auto',
      onComplete: verder,
    });

    setTimeout(verder, 600);
  }

  function toon(nummer, { push = true } = {}) {
    const vorige = stappen.find((stap) => !stap.hidden);
    const volgende = stappen.find((stap) => Number(stap.dataset.quizStep) === nummer);

    if (gestart && vorige && volgende && vorige !== volgende) {
      animeerUit(vorige, () => wissel(nummer, { push }));

      return;
    }

    wissel(nummer, { push });
  }

  function wissel(nummer, { push = true } = {}) {
    gestart = true;
    bezig = false;

    // Terug naar een vraag: de foto komt weer terug.
    root.classList.remove('quiz--uitslag');

    stappen.forEach((stap) => {
      const actief = Number(stap.dataset.quizStep) === nummer;

      stap.hidden = !actief;
      stap.classList.toggle('is--active', actief);

      if (actief) {
        animeerIn(stap);
      }
    });

    uitslagen.forEach((uitslag) => {
      uitslag.hidden = true;
    });

    huidig = nummer;

    const totaal = stappen.length;

    if (balk) {
      balk.style.transform = `scaleX(${nummer / totaal})`;
    }

    if (label) {
      label.textContent = `${nummer} van ${totaal}`;
    }

    if (voortgang) {
      voortgang.hidden = false;
      voortgang.setAttribute('aria-valuenow', String(nummer));
    }

    if (push) {
      history.pushState({ stap: nummer }, '', `?vraag=${nummer}`);
    }

    meet('routewijzer_vraag', { vraag: nummer });
  }

  function toonUitslag({ push = true } = {}) {
    bezig = false;

    const sleutel = bepaalUitslag();

    // De uitslag staat op de lichte paginakleur; zie common/quiz.scss voor waarom.
    root.classList.add('quiz--uitslag');

    stappen.forEach((stap) => {
      stap.hidden = true;
    });

    uitslagen.forEach((uitslag) => {
      uitslag.hidden = uitslag.dataset.quizResult !== sleutel;
    });

    if (voortgang) {
      voortgang.hidden = true;
    }

    vulActies(sleutel);
    vulFormulieren(antwoorden, sleutel);

    if (push) {
      history.pushState({ uitslag: sleutel }, '', `uitslag/${sleutel}/`);
    }

    meet('routewijzer_uitslag', { uitslag: sleutel });
  }

  /**
   * Alleen vraag 1 en 2 bepalen de uitslag. Dezelfde volgorde als in app/Quiz.php --
   * wijzig je die daar, wijzig hem dan hier ook.
   */
  function bepaalUitslag() {
    const lane = antwoorden.lane;
    const exp = Number(antwoorden.exp || 0);

    if (lane === 'learn') return 'leerling';
    if (lane === 'hands') return exp >= 3 ? 'voorman' : 'timmerman';
    if (lane === 'lead') return exp >= 2 ? 'uitvoerder' : 'voorman';
    if (lane === 'office') return exp >= 3 ? 'projectleider' : 'werkvoorbereider';

    return 'timmerman';
  }

  /**
   * Dezelfde knop als overal op de site: de opbouw van components/button.blade.php --
   * twee volledige gezichten die op hover om de horizontale as draaien. Omdat de
   * knoppen onder de uitslag uit script komen en niet uit een view, staat die opbouw
   * hier nog een keer; wijzigt het component, wijzig dan ook dit.
   */
  function maakKnop(label, variant) {
    const el = document.createElement('a');

    el.dataset.button = '';
    el.className = `button button--${variant}`;
    el.innerHTML = `
      <span class="button__inner">
        <span class="button__default">
          <span class="button__default-bg"></span>
          <span class="button__default-inner"><span class="button__text"></span></span>
        </span>

        <span aria-hidden="true" class="button__hover">
          <span class="button__hover-bg"></span>
          <span class="button__hover-inner"><span class="button__text"></span></span>
        </span>
      </span>
    `;

    // Via textContent en niet in de sjabloon hierboven: een label uit de instellingen
    // mag geen opmaak worden.
    el.querySelectorAll('.button__text').forEach((tekst) => {
      tekst.textContent = label;
    });

    return el;
  }

  /**
   * De twee knoppen onder de uitslag hangen aan vraag 6. De teksten staan in
   * config/quiz.php en komen via een data-attribuut mee.
   */
  function vulActies(sleutel) {
    const houder = root.querySelector(`[data-quiz-result="${sleutel}"] [data-quiz-actions]`);

    if (!houder) {
      return;
    }

    const acties = JSON.parse(root.dataset.quizActions || '{}');
    const set = acties[antwoorden.tmp] || acties.kijk || [];

    houder.innerHTML = '';

    // De uitslagkaart is wit, dus de tweede knop is omlijnd in blauw en niet in wit.
    set.forEach((actie, i) => {
      const el = maakKnop(actie.label, i === 0 ? 'secondary' : 'ghost-primary');

      if (actie.type === 'tel') {
        el.href = `tel:${(root.dataset.quizPhone || '').replace(/\s/g, '')}`;
      } else {
        el.href = `#quiz-formulier-${sleutel}`;
        el.addEventListener('click', (e) => {
          e.preventDefault();
          const form = root.querySelector(`[data-quiz-result="${sleutel}"] [data-quiz-form]`);

          if (form) {
            form.classList.add('is--open');
            form.querySelector('input[name="naam"]').focus();
          }
        });
      }

      el.addEventListener('click', () => meet('routewijzer_knop', { knop: actie.label }));
      houder.appendChild(el);
    });
  }

  /**
   * De antwoorden gaan onzichtbaar mee met het formulier, zodat Henk voor het
   * telefoongesprek al weet hoe ver iemand wil rijden en wanneer hij kan beginnen.
   */
  function vulFormulieren(waarden, sleutel = null) {
    const formulieren = sleutel
      ? root.querySelectorAll(`[data-quiz-result="${sleutel}"] [data-quiz-form]`)
      : root.querySelectorAll('[data-quiz-form]');

    formulieren.forEach((form) => {
      form.querySelectorAll('[data-quiz-context]').forEach((veld) => {
        const naam = veld.dataset.quizContext;

        veld.value = naam === 'bron' ? (document.referrer || 'direct') : (waarden[naam] || '');
      });

      form.addEventListener('submit', () => meet('routewijzer_formulier'), { once: true });
    });
  }

  /**
   * Een stap verder: bij de laatste vraag is dat de uitslag.
   */
  function verderVanaf(nummer) {
    if (nummer === stappen.length) {
      toonUitslag();
    } else {
      toon(nummer + 1);
    }
  }

  stappen.forEach((stap) => {
    const sleutel = stap.dataset.quizKey;
    const opties = [...stap.querySelectorAll('[data-quiz-option]')];
    const verder = stap.querySelector('[data-quiz-next]');
    const terug = stap.querySelector('[data-quiz-prev]');

    opties.forEach((optie) => {
      optie.addEventListener('click', () => {
        if (bezig) {
          return;
        }

        opties.forEach((o) => o.setAttribute('aria-checked', String(o === optie)));

        antwoorden[sleutel] = optie.dataset.quizOption;
        bewaar();

        // Een klein zetje op de toets: het bevestigt de keuze op de plek waar je hem
        // maakt, en dat is precies waar je op dat moment kijkt.
        if (!minderBeweging()) {
          gsap.fromTo(optie.querySelector('.quiz__key'), { scale: .8 }, {
            scale: 1, duration: .6, ease: 'elastic.out(1, 0.6)', overwrite: 'auto',
          });
        }

        verder.disabled = false;

        // Het antwoord is de keuze: nog een klik op Volgende voegt daar niets aan toe en
        // verdubbelt het aantal handelingen van een test van zes vragen. De knop blijft
        // wel staan -- wie via Vorige terugkomt, heeft al gekozen en moet er weer langs.
        bezig = true;

        const nummer = Number(stap.dataset.quizStep);

        setTimeout(() => {
          // Wie in die paar tienden de terugknop pakt, staat al ergens anders; dan is deze
          // klik ingehaald en hoort hij niet alsnog een scherm op te schuiven.
          if (huidig === nummer) {
            verderVanaf(nummer);
          }
        }, minderBeweging() ? BEVESTIGING_RUSTIG : BEVESTIGING);
      });
    });

    verder.addEventListener('click', () => {
      if (bezig) {
        return;
      }

      verderVanaf(Number(stap.dataset.quizStep));
    });

    if (terug) {
      terug.addEventListener('click', () => {
        if (bezig) {
          return;
        }

        toon(Number(stap.dataset.quizStep) - 1);
      });
    }
  });

  // Sneltoetsen: A tot en met D kiest een antwoord, Enter gaat verder. Een test van zes
  // vragen laat zich zo in een paar aanslagen doorlopen, en de letters staan bij de
  // antwoorden zelf, dus je hoeft er niets voor te weten.
  document.addEventListener('keydown', (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey) {
      return;
    }

    // Staat de aandacht nergens, dan is het doel het document zelf en dat kent geen
    // closest(). Zonder deze controle sneuvelt de hele afhandeling op de eerste aanslag.
    const doel = event.target instanceof Element ? event.target : null;

    // In een invulveld hoort een letter gewoon een letter te zijn.
    if (doel && doel.closest('input, select, textarea')) {
      return;
    }

    const stap = stappen.find((s) => !s.hidden);

    if (!stap) {
      return;
    }

    if (event.key === 'Enter') {
      // Staat de aandacht op een antwoord, dan doet die knop het zelf al.
      if (doel && doel.closest('[data-quiz-option]')) {
        return;
      }

      const verder = stap.querySelector('[data-quiz-next]');

      if (verder && !verder.disabled) {
        event.preventDefault();
        verder.click();
      }

      return;
    }

    const optie = stap.querySelector(`[data-quiz-option][data-quiz-letter="${event.key.toUpperCase()}"]`);

    if (optie) {
      event.preventDefault();
      optie.click();
    }
  });

  root.querySelectorAll('[data-quiz-restart]').forEach((knop) => {
    knop.addEventListener('click', () => {
      Object.keys(antwoorden).forEach((k) => delete antwoorden[k]);
      bewaar();

      root.querySelectorAll('[data-quiz-option]').forEach((o) => o.setAttribute('aria-checked', 'false'));
      root.querySelectorAll('[data-quiz-next]').forEach((b) => { b.disabled = true; });

      toon(1);
    });
  });

  // De terugknop van de browser hoort gewoon een vraag terug te gaan.
  window.addEventListener('popstate', (e) => {
    const staat = e.state || {};

    if (staat.uitslag) {
      toonUitslag({ push: false });
    } else {
      toon(staat.stap || 1, { push: false });
    }
  });

  // Hervatten waar iemand gebleven was: het eerste onbeantwoorde scherm.
  const eersteOnbeantwoord = stappen.findIndex((stap) => !antwoorden[stap.dataset.quizKey]);
  const start = eersteOnbeantwoord === -1 ? stappen.length : eersteOnbeantwoord + 1;

  stappen.forEach((stap) => {
    const gekozen = antwoorden[stap.dataset.quizKey];

    if (!gekozen) {
      return;
    }

    const optie = stap.querySelector(`[data-quiz-option="${gekozen}"]`);

    if (optie) {
      optie.setAttribute('aria-checked', 'true');
      stap.querySelector('[data-quiz-next]').disabled = false;
    }
  });

  toon(start, { push: false });
  meet('routewijzer_gestart');
}
